'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import ScoreGauge from '@/components/tools/ScoreGauge';
import ProgressBar from '@/components/tools/ProgressBar';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface FormData {
  // Financial
  emergencyFundMonths: number;
  monthlyExpenses: number;
  severanceLikelihood: string;
  otherIncomeSources: number;
  // Job Market Readiness
  resumeUpdated: string;
  linkedinUpdated: string;
  portfolioWebsite: string;
  skillsInDemand: string;
  industryGrowthTrend: string;
  // Network
  activeLinkedinConnections: string;
  industryContacts: string;
  recentNetworkingActivity: string;
  referencesAvailable: string;
  // Skills
  currentRoleDemand: string;
  transferableSkills: string;
  recentCertifications: string;
  sideProjects: string;
}

interface CategoryScore {
  label: string;
  score: number;
  color: string;
}

interface ActionItem {
  priority: 'high' | 'medium' | 'low';
  category: string;
  action: string;
  timeline: 'immediate' | '30-day' | '90-day';
}

interface Results {
  overall: number;
  categories: CategoryScore[];
  actionPlan: ActionItem[];
  financialRunway: string;
}

/* ------------------------------------------------------------------ */
/*  Default form values                                                */
/* ------------------------------------------------------------------ */

const DEFAULT_FORM: FormData = {
  emergencyFundMonths: 3,
  monthlyExpenses: 4000,
  severanceLikelihood: 'possible',
  otherIncomeSources: 0,
  resumeUpdated: 'no',
  linkedinUpdated: 'no',
  portfolioWebsite: 'no',
  skillsInDemand: 'somewhat',
  industryGrowthTrend: 'stable',
  activeLinkedinConnections: '100-500',
  industryContacts: '1-5',
  recentNetworkingActivity: 'rarely',
  referencesAvailable: '1-2',
  currentRoleDemand: 'moderate',
  transferableSkills: '3-5',
  recentCertifications: 'none',
  sideProjects: 'no',
};

/* ------------------------------------------------------------------ */
/*  Scoring functions                                                  */
/* ------------------------------------------------------------------ */

function scoreFinancial(form: FormData): number {
  let score = 0;

  // Emergency fund (0-40 points)
  if (form.emergencyFundMonths >= 12) score += 40;
  else if (form.emergencyFundMonths >= 9) score += 35;
  else if (form.emergencyFundMonths >= 6) score += 28;
  else if (form.emergencyFundMonths >= 3) score += 18;
  else if (form.emergencyFundMonths >= 1) score += 8;
  else score += 0;

  // Severance likelihood (0-30 points)
  if (form.severanceLikelihood === 'very-likely') score += 30;
  else if (form.severanceLikelihood === 'likely') score += 22;
  else if (form.severanceLikelihood === 'possible') score += 12;
  else if (form.severanceLikelihood === 'unlikely') score += 4;
  else score += 0;

  // Other income sources (0-30 points)
  if (form.otherIncomeSources >= 3) score += 30;
  else if (form.otherIncomeSources >= 2) score += 22;
  else if (form.otherIncomeSources >= 1) score += 14;
  else score += 0;

  return Math.min(100, score);
}

function scoreMarketReadiness(form: FormData): number {
  let score = 0;

  // Resume updated (0-25 points)
  if (form.resumeUpdated === 'yes') score += 25;

  // LinkedIn updated (0-20 points)
  if (form.linkedinUpdated === 'yes') score += 20;

  // Portfolio/website (0-15 points)
  if (form.portfolioWebsite === 'yes') score += 15;

  // Skills in demand (0-25 points)
  if (form.skillsInDemand === 'highly') score += 25;
  else if (form.skillsInDemand === 'somewhat') score += 15;
  else if (form.skillsInDemand === 'not-sure') score += 8;
  else score += 0;

  // Industry growth trend (0-15 points)
  if (form.industryGrowthTrend === 'growing') score += 15;
  else if (form.industryGrowthTrend === 'stable') score += 10;
  else if (form.industryGrowthTrend === 'declining') score += 3;

  return Math.min(100, score);
}

function scoreNetwork(form: FormData): number {
  let score = 0;

  // Active LinkedIn connections (0-25 points)
  if (form.activeLinkedinConnections === '500+') score += 25;
  else if (form.activeLinkedinConnections === '100-500') score += 18;
  else if (form.activeLinkedinConnections === '50-100') score += 10;
  else score += 4;

  // Industry contacts (0-25 points)
  if (form.industryContacts === '10+') score += 25;
  else if (form.industryContacts === '6-10') score += 18;
  else if (form.industryContacts === '1-5') score += 10;
  else score += 0;

  // Recent networking activity (0-25 points)
  if (form.recentNetworkingActivity === 'weekly') score += 25;
  else if (form.recentNetworkingActivity === 'monthly') score += 18;
  else if (form.recentNetworkingActivity === 'rarely') score += 8;
  else score += 0;

  // References available (0-25 points)
  if (form.referencesAvailable === '5+') score += 25;
  else if (form.referencesAvailable === '3-4') score += 20;
  else if (form.referencesAvailable === '1-2') score += 12;
  else score += 2;

  return Math.min(100, score);
}

function scoreSkills(form: FormData): number {
  let score = 0;

  // Current role demand (0-30 points)
  if (form.currentRoleDemand === 'high') score += 30;
  else if (form.currentRoleDemand === 'moderate') score += 18;
  else if (form.currentRoleDemand === 'low') score += 6;
  else score += 0;

  // Transferable skills (0-25 points)
  if (form.transferableSkills === '8+') score += 25;
  else if (form.transferableSkills === '6-7') score += 20;
  else if (form.transferableSkills === '3-5') score += 14;
  else if (form.transferableSkills === '1-2') score += 6;
  else score += 0;

  // Recent certifications/training (0-25 points)
  if (form.recentCertifications === 'multiple') score += 25;
  else if (form.recentCertifications === 'one') score += 16;
  else if (form.recentCertifications === 'in-progress') score += 10;
  else score += 0;

  // Side projects (0-20 points)
  if (form.sideProjects === 'active') score += 20;
  else if (form.sideProjects === 'past') score += 12;
  else score += 0;

  return Math.min(100, score);
}

function buildActionPlan(
  form: FormData,
  financial: number,
  market: number,
  network: number,
  skills: number,
): ActionItem[] {
  const items: ActionItem[] = [];

  // Financial actions
  if (form.emergencyFundMonths < 6) {
    items.push({
      priority: form.emergencyFundMonths < 3 ? 'high' : 'medium',
      category: 'Financial',
      action: `Build your emergency fund to at least 6 months of expenses ($${(form.monthlyExpenses * 6).toLocaleString()}). Start by automating a monthly transfer, even if small.`,
      timeline: form.emergencyFundMonths < 3 ? 'immediate' : '90-day',
    });
  }
  if (form.otherIncomeSources === 0) {
    items.push({
      priority: 'medium',
      category: 'Financial',
      action: 'Explore additional income streams: freelancing, consulting, teaching, or a side project that leverages your current skills.',
      timeline: '30-day',
    });
  }
  if (form.severanceLikelihood === 'unlikely' || form.severanceLikelihood === 'unknown') {
    items.push({
      priority: 'low',
      category: 'Financial',
      action: 'Review your employment contract for severance terms. Understand your company\'s layoff policies and any benefits you would be entitled to.',
      timeline: '30-day',
    });
  }

  // Market readiness actions
  if (form.resumeUpdated === 'no') {
    items.push({
      priority: 'high',
      category: 'Market Readiness',
      action: 'Update your resume immediately. Include your most recent accomplishments, quantified results, and keywords from target job descriptions.',
      timeline: 'immediate',
    });
  }
  if (form.linkedinUpdated === 'no') {
    items.push({
      priority: 'high',
      category: 'Market Readiness',
      action: 'Update your LinkedIn profile: headline, summary, experience, and skills. Turn on "Open to Work" privately for recruiters.',
      timeline: 'immediate',
    });
  }
  if (form.portfolioWebsite === 'no') {
    items.push({
      priority: 'medium',
      category: 'Market Readiness',
      action: 'Create a simple portfolio or personal website showcasing your best work, projects, and testimonials.',
      timeline: '30-day',
    });
  }
  if (form.skillsInDemand === 'not-really' || form.skillsInDemand === 'not-sure') {
    items.push({
      priority: 'high',
      category: 'Market Readiness',
      action: 'Research which skills are most in-demand for your target roles on job boards. Identify the top 2-3 skill gaps and start filling them.',
      timeline: 'immediate',
    });
  }
  if (form.industryGrowthTrend === 'declining') {
    items.push({
      priority: 'high',
      category: 'Market Readiness',
      action: 'Your industry is contracting. Research adjacent industries where your skills transfer and start networking into those spaces.',
      timeline: 'immediate',
    });
  }

  // Network actions
  if (form.recentNetworkingActivity === 'never' || form.recentNetworkingActivity === 'rarely') {
    items.push({
      priority: 'high',
      category: 'Network',
      action: 'Start networking now. Reach out to 3-5 former colleagues or industry contacts this week. A warm network is your best job search asset.',
      timeline: 'immediate',
    });
  }
  if (form.industryContacts === 'none' || form.industryContacts === '1-5') {
    items.push({
      priority: 'medium',
      category: 'Network',
      action: 'Expand your industry network: attend virtual or local meetups, join professional associations, and engage in LinkedIn groups.',
      timeline: '30-day',
    });
  }
  if (form.referencesAvailable === 'none' || form.referencesAvailable === '1-2') {
    items.push({
      priority: 'medium',
      category: 'Network',
      action: 'Line up 3-5 professional references. Contact them proactively, brief them on your strengths, and confirm their willingness to advocate for you.',
      timeline: '30-day',
    });
  }
  if (form.activeLinkedinConnections === 'under-50') {
    items.push({
      priority: 'low',
      category: 'Network',
      action: 'Grow your LinkedIn network strategically. Connect with recruiters in your field, former colleagues, and professionals at target companies.',
      timeline: '90-day',
    });
  }

  // Skills actions
  if (form.recentCertifications === 'none') {
    items.push({
      priority: 'medium',
      category: 'Skills',
      action: 'Pursue a relevant certification or complete an online course in a high-demand skill for your field. This signals growth to employers.',
      timeline: '30-day',
    });
  }
  if (form.currentRoleDemand === 'low' || form.currentRoleDemand === 'declining') {
    items.push({
      priority: 'high',
      category: 'Skills',
      action: 'Your current role has declining demand. Identify transferable skills and upskill into roles with stronger market demand.',
      timeline: 'immediate',
    });
  }
  if (form.sideProjects === 'no') {
    items.push({
      priority: 'low',
      category: 'Skills',
      action: 'Start a side project, contribute to open-source, or volunteer your skills. This builds your portfolio and demonstrates initiative.',
      timeline: '90-day',
    });
  }
  if (form.transferableSkills === 'none' || form.transferableSkills === '1-2') {
    items.push({
      priority: 'medium',
      category: 'Skills',
      action: 'Identify and develop more transferable skills (leadership, communication, data analysis, project management) that work across industries.',
      timeline: '30-day',
    });
  }

  // Sort by priority: high first, then medium, then low
  const priorityOrder = { high: 0, medium: 1, low: 2 };
  items.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

  return items;
}

function calculateResults(form: FormData): Results {
  const financial = scoreFinancial(form);
  const market = scoreMarketReadiness(form);
  const network = scoreNetwork(form);
  const skills = scoreSkills(form);

  // Weighted overall: financial 30%, market 30%, network 20%, skills 20%
  const overall = Math.round(
    financial * 0.3 + market * 0.3 + network * 0.2 + skills * 0.2,
  );

  const categories: CategoryScore[] = [
    { label: 'Financial Runway', score: financial, color: 'text-amber-600' },
    { label: 'Market Readiness', score: market, color: 'text-blue-600' },
    { label: 'Network Strength', score: network, color: 'text-emerald-600' },
    { label: 'Skills Currency', score: skills, color: 'text-purple-600' },
  ];

  const actionPlan = buildActionPlan(form, financial, market, network, skills);

  // Compute financial runway description
  const totalSavings = form.emergencyFundMonths * form.monthlyExpenses;
  const monthsRunway = form.monthlyExpenses > 0
    ? Math.round(totalSavings / form.monthlyExpenses)
    : 0;
  const financialRunway =
    monthsRunway >= 12
      ? `${monthsRunway}+ months -- you have a strong financial cushion.`
      : monthsRunway >= 6
        ? `~${monthsRunway} months -- a solid foundation, but consider growing it further.`
        : monthsRunway >= 3
          ? `~${monthsRunway} months -- enough for a focused search, but tight. Build this up.`
          : `~${monthsRunway} month${monthsRunway !== 1 ? 's' : ''} -- critically low. Building savings should be your top priority.`;

  return { overall, categories, actionPlan, financialRunway };
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

function SectionHeader({ number, title, icon }: { number: number; title: string; icon: string }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shrink-0">
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
        </svg>
      </div>
      <div>
        <p className="text-xs font-bold text-amber-600 uppercase tracking-wider">Section {number} of 4</p>
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
      </div>
    </div>
  );
}

function FormLabel({ htmlFor, children, hint }: { htmlFor: string; children: React.ReactNode; hint?: string }) {
  return (
    <div className="mb-2">
      <label htmlFor={htmlFor} className="block text-sm font-semibold text-gray-700">
        {children}
      </label>
      {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
    </div>
  );
}

function PriorityBadge({ priority }: { priority: 'high' | 'medium' | 'low' }) {
  const styles = {
    high: 'bg-red-100 text-red-700',
    medium: 'bg-amber-100 text-amber-700',
    low: 'bg-blue-100 text-blue-700',
  };
  return (
    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${styles[priority]}`}>
      {priority === 'high' ? 'High Priority' : priority === 'medium' ? 'Medium' : 'Low Priority'}
    </span>
  );
}

function TimelineBadge({ timeline }: { timeline: 'immediate' | '30-day' | '90-day' }) {
  const labels = {
    immediate: 'Do Now',
    '30-day': 'Within 30 Days',
    '90-day': 'Within 90 Days',
  };
  return (
    <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
      {labels[timeline]}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function LayoffPrepContent() {
  const [form, setForm] = useState<FormData>(DEFAULT_FORM);
  const [results, setResults] = useState<Results | null>(null);

  function update<K extends keyof FormData>(key: K, value: FormData[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleCalculate() {
    const r = calculateResults(form);
    setResults(r);
    // Scroll to results
    setTimeout(() => {
      document.getElementById('results-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }

  function handleReset() {
    setForm(DEFAULT_FORM);
    setResults(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const selectClass =
    'w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition bg-white text-gray-900';
  const inputClass =
    'w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition';

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-stone-100">
      <ToolHero
        title="Layoff Preparedness Calculator"
        description="Assess your readiness for a potential layoff and get a personalized action plan. Evaluate your financial runway, job market readiness, network strength, and skills currency."
        category="Career Tools"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Introduction card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shrink-0">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">How It Works</h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                Answer questions across four key areas: financial safety net, job market readiness, professional network, and skills currency.
                We will calculate your overall preparedness score and generate a prioritized action plan based on your weakest areas.
                Everything runs in your browser -- your data is never sent anywhere.
              </p>
              <p className="mt-4 pt-3 border-t border-gray-200 text-xs text-gray-500">
                <strong>Sources &amp; Further Reading:</strong>{' '}
                <a href="https://www.careeronestop.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">CareerOneStop - Layoff &amp; Job Transition Resources (U.S. DOL)</a>
                {' · '}
                <a href="https://www.bls.gov/ooh/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Bureau of Labor Statistics - Occupational Outlook</a>
                {' · '}
                <a href="https://www.mynextmove.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">My Next Move - Career Exploration (U.S. DOL)</a>
              </p>
            </div>
          </div>
        </div>

        {/* ============================================================ */}
        {/*  QUESTIONNAIRE                                                */}
        {/* ============================================================ */}
        <div className="space-y-8">
          {/* ------- Section 1: Financial ------- */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <SectionHeader
              number={1}
              title="Financial Safety Net"
              icon="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Emergency fund months */}
              <div>
                <FormLabel htmlFor="emergencyFundMonths">
                  Emergency Fund (months of expenses)
                </FormLabel>
                <div className="flex items-center gap-4">
                  <input
                    id="emergencyFundMonths"
                    type="range"
                    min={0}
                    max={12}
                    value={form.emergencyFundMonths}
                    onChange={(e) => update('emergencyFundMonths', Number(e.target.value))}
                    className="flex-1 accent-amber-600 h-2 rounded-lg cursor-pointer"
                  />
                  <span className="w-20 text-center text-lg font-bold text-amber-700 bg-amber-50 rounded-lg py-1">
                    {form.emergencyFundMonths}{form.emergencyFundMonths >= 12 ? '+' : ''} mo
                  </span>
                </div>
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>0</span>
                  <span>6</span>
                  <span>12+</span>
                </div>
              </div>

              {/* Monthly expenses */}
              <div>
                <FormLabel htmlFor="monthlyExpenses" hint="Rent, bills, groceries, subscriptions, etc.">
                  Monthly Expenses Estimate
                </FormLabel>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
                  <input
                    id="monthlyExpenses"
                    type="number"
                    min={0}
                    value={form.monthlyExpenses}
                    onChange={(e) => update('monthlyExpenses', Number(e.target.value))}
                    className={`${inputClass} pl-8`}
                  />
                </div>
              </div>

              {/* Severance likelihood */}
              <div>
                <FormLabel htmlFor="severanceLikelihood">
                  Severance Package Likelihood
                </FormLabel>
                <select
                  id="severanceLikelihood"
                  value={form.severanceLikelihood}
                  onChange={(e) => update('severanceLikelihood', e.target.value)}
                  className={selectClass}
                >
                  <option value="very-likely">Very Likely (company has history of severance)</option>
                  <option value="likely">Likely</option>
                  <option value="possible">Possible</option>
                  <option value="unlikely">Unlikely</option>
                  <option value="unknown">Not Sure</option>
                </select>
              </div>

              {/* Other income sources */}
              <div>
                <FormLabel htmlFor="otherIncomeSources" hint="Freelance, spouse income, rental, investments, etc.">
                  Other Income Sources (count)
                </FormLabel>
                <select
                  id="otherIncomeSources"
                  value={form.otherIncomeSources}
                  onChange={(e) => update('otherIncomeSources', Number(e.target.value))}
                  className={selectClass}
                >
                  <option value={0}>None -- primary job is my only income</option>
                  <option value={1}>1 additional source</option>
                  <option value={2}>2 additional sources</option>
                  <option value={3}>3 or more additional sources</option>
                </select>
              </div>
            </div>
          </div>

          {/* ------- Section 2: Job Market Readiness ------- */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <SectionHeader
              number={2}
              title="Job Market Readiness"
              icon="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Resume updated */}
              <div>
                <FormLabel htmlFor="resumeUpdated">Resume Updated in Last 3 Months?</FormLabel>
                <select
                  id="resumeUpdated"
                  value={form.resumeUpdated}
                  onChange={(e) => update('resumeUpdated', e.target.value)}
                  className={selectClass}
                >
                  <option value="yes">Yes -- it is current and tailored</option>
                  <option value="no">No -- it needs updating</option>
                </select>
              </div>

              {/* LinkedIn updated */}
              <div>
                <FormLabel htmlFor="linkedinUpdated">LinkedIn Profile Updated Recently?</FormLabel>
                <select
                  id="linkedinUpdated"
                  value={form.linkedinUpdated}
                  onChange={(e) => update('linkedinUpdated', e.target.value)}
                  className={selectClass}
                >
                  <option value="yes">Yes -- headline, summary, and experience are current</option>
                  <option value="no">No -- it is outdated</option>
                </select>
              </div>

              {/* Portfolio/website */}
              <div>
                <FormLabel htmlFor="portfolioWebsite">Portfolio or Personal Website?</FormLabel>
                <select
                  id="portfolioWebsite"
                  value={form.portfolioWebsite}
                  onChange={(e) => update('portfolioWebsite', e.target.value)}
                  className={selectClass}
                >
                  <option value="yes">Yes -- up to date with recent work</option>
                  <option value="no">No -- I do not have one</option>
                </select>
              </div>

              {/* Skills in demand */}
              <div>
                <FormLabel htmlFor="skillsInDemand">Are Your Core Skills In Demand?</FormLabel>
                <select
                  id="skillsInDemand"
                  value={form.skillsInDemand}
                  onChange={(e) => update('skillsInDemand', e.target.value)}
                  className={selectClass}
                >
                  <option value="highly">Highly -- I see many job postings needing my skills</option>
                  <option value="somewhat">Somewhat -- some demand, not overwhelming</option>
                  <option value="not-sure">Not sure -- I have not checked recently</option>
                  <option value="not-really">Not really -- my skills are becoming niche</option>
                </select>
              </div>

              {/* Industry growth trend */}
              <div className="md:col-span-2">
                <FormLabel htmlFor="industryGrowthTrend">Your Industry's Growth Trend</FormLabel>
                <select
                  id="industryGrowthTrend"
                  value={form.industryGrowthTrend}
                  onChange={(e) => update('industryGrowthTrend', e.target.value)}
                  className={selectClass}
                >
                  <option value="growing">Growing -- lots of hiring and investment</option>
                  <option value="stable">Stable -- holding steady</option>
                  <option value="declining">Declining -- layoffs and contraction</option>
                </select>
              </div>
            </div>
          </div>

          {/* ------- Section 3: Network ------- */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <SectionHeader
              number={3}
              title="Network Strength"
              icon="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Active LinkedIn connections */}
              <div>
                <FormLabel htmlFor="activeLinkedinConnections">Active LinkedIn Connections</FormLabel>
                <select
                  id="activeLinkedinConnections"
                  value={form.activeLinkedinConnections}
                  onChange={(e) => update('activeLinkedinConnections', e.target.value)}
                  className={selectClass}
                >
                  <option value="500+">500+ connections</option>
                  <option value="100-500">100 - 500 connections</option>
                  <option value="50-100">50 - 100 connections</option>
                  <option value="under-50">Under 50 connections</option>
                </select>
              </div>

              {/* Industry contacts */}
              <div>
                <FormLabel htmlFor="industryContacts" hint="People you could call for a favor or referral">
                  Close Industry Contacts
                </FormLabel>
                <select
                  id="industryContacts"
                  value={form.industryContacts}
                  onChange={(e) => update('industryContacts', e.target.value)}
                  className={selectClass}
                >
                  <option value="10+">10 or more</option>
                  <option value="6-10">6 - 10</option>
                  <option value="1-5">1 - 5</option>
                  <option value="none">None</option>
                </select>
              </div>

              {/* Recent networking activity */}
              <div>
                <FormLabel htmlFor="recentNetworkingActivity">Recent Networking Activity</FormLabel>
                <select
                  id="recentNetworkingActivity"
                  value={form.recentNetworkingActivity}
                  onChange={(e) => update('recentNetworkingActivity', e.target.value)}
                  className={selectClass}
                >
                  <option value="weekly">Weekly -- I actively engage with my network</option>
                  <option value="monthly">Monthly -- occasional outreach</option>
                  <option value="rarely">Rarely -- a few times a year</option>
                  <option value="never">Never -- I have not networked in a long time</option>
                </select>
              </div>

              {/* References available */}
              <div>
                <FormLabel htmlFor="referencesAvailable">Professional References Available</FormLabel>
                <select
                  id="referencesAvailable"
                  value={form.referencesAvailable}
                  onChange={(e) => update('referencesAvailable', e.target.value)}
                  className={selectClass}
                >
                  <option value="5+">5 or more -- strong advocates</option>
                  <option value="3-4">3 - 4 references</option>
                  <option value="1-2">1 - 2 references</option>
                  <option value="none">None lined up</option>
                </select>
              </div>
            </div>
          </div>

          {/* ------- Section 4: Skills ------- */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <SectionHeader
              number={4}
              title="Skills Currency"
              icon="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Current role demand */}
              <div>
                <FormLabel htmlFor="currentRoleDemand">Demand for Your Current Role/Title</FormLabel>
                <select
                  id="currentRoleDemand"
                  value={form.currentRoleDemand}
                  onChange={(e) => update('currentRoleDemand', e.target.value)}
                  className={selectClass}
                >
                  <option value="high">High -- many open positions</option>
                  <option value="moderate">Moderate -- some openings</option>
                  <option value="low">Low -- few positions available</option>
                  <option value="declining">Declining -- roles being eliminated</option>
                </select>
              </div>

              {/* Transferable skills */}
              <div>
                <FormLabel htmlFor="transferableSkills" hint="Leadership, data analysis, writing, project management, etc.">
                  Transferable Skills Count
                </FormLabel>
                <select
                  id="transferableSkills"
                  value={form.transferableSkills}
                  onChange={(e) => update('transferableSkills', e.target.value)}
                  className={selectClass}
                >
                  <option value="8+">8 or more</option>
                  <option value="6-7">6 - 7</option>
                  <option value="3-5">3 - 5</option>
                  <option value="1-2">1 - 2</option>
                  <option value="none">Not sure / none identified</option>
                </select>
              </div>

              {/* Recent certifications */}
              <div>
                <FormLabel htmlFor="recentCertifications">Recent Certifications or Training (last 12 months)</FormLabel>
                <select
                  id="recentCertifications"
                  value={form.recentCertifications}
                  onChange={(e) => update('recentCertifications', e.target.value)}
                  className={selectClass}
                >
                  <option value="multiple">Multiple completed</option>
                  <option value="one">One completed</option>
                  <option value="in-progress">Currently in progress</option>
                  <option value="none">None</option>
                </select>
              </div>

              {/* Side projects */}
              <div>
                <FormLabel htmlFor="sideProjects">Side Projects or Freelance Work</FormLabel>
                <select
                  id="sideProjects"
                  value={form.sideProjects}
                  onChange={(e) => update('sideProjects', e.target.value)}
                  className={selectClass}
                >
                  <option value="active">Yes -- actively working on something</option>
                  <option value="past">I have done some in the past</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>
          </div>

          {/* Calculate button */}
          <div>
            <button
              onClick={handleCalculate}
              className="w-full bg-gradient-to-r from-amber-600 to-amber-500 text-white font-bold py-4 px-8 rounded-xl shadow-lg hover:from-amber-700 hover:to-amber-600 transition-all text-lg cursor-pointer flex items-center justify-center gap-3"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Calculate My Preparedness Score
            </button>
          </div>
        </div>

        {/* ============================================================ */}
        {/*  RESULTS                                                      */}
        {/* ============================================================ */}
        {results && (
          <div id="results-section" className="mt-12 space-y-8">
            {/* Overall Score */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Your Layoff Preparedness Score
              </h2>
              <div className="flex justify-center mb-6">
                <ScoreGauge score={results.overall} label="Overall Preparedness" size={240} />
              </div>
              <p className="text-center text-gray-600 max-w-lg mx-auto">
                {results.overall >= 80
                  ? 'You are well-prepared for a potential layoff. Your financial, professional, and networking foundations are strong.'
                  : results.overall >= 60
                    ? 'You have a solid foundation but there are areas to strengthen. Follow the action plan below to improve your readiness.'
                    : results.overall >= 40
                      ? 'You have some preparation in place, but significant gaps remain. Focus on the high-priority actions below to get ready.'
                      : 'Your preparedness is low. A layoff could be very disruptive right now. Start with the immediate actions below to build your safety net.'}
              </p>
            </div>

            {/* Category Breakdown */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Category Breakdown</h3>
              <div className="space-y-5">
                {results.categories.map((cat) => (
                  <ProgressBar key={cat.label} value={cat.score} label={cat.label} />
                ))}
              </div>
            </div>

            {/* Financial Runway */}
            <div className="bg-amber-50 rounded-2xl border border-amber-200 p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-amber-900 mb-1">Your Financial Runway</h3>
                  <p className="text-amber-800 leading-relaxed">
                    Based on ${form.monthlyExpenses.toLocaleString()}/month in expenses and {form.emergencyFundMonths} month{form.emergencyFundMonths !== 1 ? 's' : ''} of savings: {results.financialRunway}
                  </p>
                </div>
              </div>
            </div>

            {/* Action Plan */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Your Personalized Action Plan
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                {results.actionPlan.length} action items prioritized by urgency. Start from the top.
              </p>

              {results.actionPlan.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-lg font-bold text-gray-900">You are in great shape!</p>
                  <p className="text-gray-500 mt-1">No critical action items. Keep maintaining your readiness.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {results.actionPlan.map((item, i) => (
                    <div
                      key={i}
                      className={`flex items-start gap-4 p-4 rounded-xl border transition-all ${
                        item.priority === 'high'
                          ? 'border-red-200 bg-red-50/50'
                          : item.priority === 'medium'
                            ? 'border-amber-200 bg-amber-50/50'
                            : 'border-gray-200 bg-gray-50/50'
                      }`}
                    >
                      <span className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-sm font-extrabold text-gray-500 shrink-0">
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">{item.category}</span>
                          <PriorityBadge priority={item.priority} />
                          <TimelineBadge timeline={item.timeline} />
                        </div>
                        <p className="text-sm text-gray-800 leading-relaxed">{item.action}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Timeline Summary */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Timeline Recommendations</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Immediate */}
                <div className="rounded-xl border-2 border-red-200 bg-red-50/50 p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-red-500 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <h4 className="font-bold text-red-900">Do Now</h4>
                  </div>
                  <p className="text-2xl font-extrabold text-red-700 mb-1">
                    {results.actionPlan.filter((a) => a.timeline === 'immediate').length}
                  </p>
                  <p className="text-xs text-red-600">urgent action{results.actionPlan.filter((a) => a.timeline === 'immediate').length !== 1 ? 's' : ''}</p>
                </div>

                {/* 30 days */}
                <div className="rounded-xl border-2 border-amber-200 bg-amber-50/50 p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h4 className="font-bold text-amber-900">30 Days</h4>
                  </div>
                  <p className="text-2xl font-extrabold text-amber-700 mb-1">
                    {results.actionPlan.filter((a) => a.timeline === '30-day').length}
                  </p>
                  <p className="text-xs text-amber-600">action{results.actionPlan.filter((a) => a.timeline === '30-day').length !== 1 ? 's' : ''} to complete</p>
                </div>

                {/* 90 days */}
                <div className="rounded-xl border-2 border-blue-200 bg-blue-50/50 p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h4 className="font-bold text-blue-900">90 Days</h4>
                  </div>
                  <p className="text-2xl font-extrabold text-blue-700 mb-1">
                    {results.actionPlan.filter((a) => a.timeline === '90-day').length}
                  </p>
                  <p className="text-xs text-blue-600">longer-term item{results.actionPlan.filter((a) => a.timeline === '90-day').length !== 1 ? 's' : ''}</p>
                </div>
              </div>
            </div>

            {/* Recalculate / Reset */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-600 to-amber-500 text-white font-bold rounded-xl shadow-lg hover:from-amber-700 hover:to-amber-600 transition-all cursor-pointer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Answers
              </button>
              <button
                onClick={handleReset}
                className="inline-flex items-center gap-2 px-6 py-3 border-2 border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Start Over
              </button>
            </div>

            {/* CTA */}
            <ToolCTA
              headline="Ready to upgrade your resume?"
              description="A strong resume is your first line of defense against a layoff. Build a polished, ATS-optimized resume in minutes with Superpower Resume."
              buttonText="Build My Resume Free"
            />
          </div>
        )}
      </div>
    </div>
  );
}
